import type { SamplingConfig } from '../types/sampling';
import type { WaypointAttributes } from '../types/scenario';
import type { WaypointType } from '../types/waypoint-type';

export interface InterpolatedPoint {
  longitude: number;
  latitude: number;
  /** Fraction of total time (0 to 1) */
  fraction: number;
  /** Bearing to the next point in degrees (0-360), or bearing from previous if last point */
  bearing_deg: number;
  /** Speed in knots at this point (from waypoint attributes) */
  sog_kn: number;
  /** Rate of turn in degrees per minute at this point */
  rot_deg_min: number;
  /** Identity overrides from the source waypoint (null = use track defaults) */
  name?: string | null;
  mmsi?: string | null;
  imo?: number | null;
  nav_status?: string | null;
  /** Heading override in degrees — when set, heading differs from COG (path bearing) */
  heading_deg?: number | null;
  /** Vessel identity overrides (null = use track source) */
  callSign?: string | null;
  flag?: string | null;
  shipType?: string | null;
  aisClass?: string | null;
  width_m?: number | null;
  length_m?: number | null;
  draught_m?: number | null;
  destination?: string | null;
  eta?: string | null;
  /** Anomaly type inherited from the source waypoint (undefined = normal). */
  waypointType?: WaypointType;
}

/**
 * Calculate the distance in meters between two WGS84 coordinates using the Haversine formula.
 */
export function haversineDistance(
  lon1: number,
  lat1: number,
  lon2: number,
  lat2: number,
): number {
  const R = 6371008.8; // Earth's mean radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Calculate the initial bearing from point 1 to point 2 in degrees (0-360).
 */
export function calculateBearing(
  lon1: number,
  lat1: number,
  lon2: number,
  lat2: number,
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);

  return ((toDeg(Math.atan2(y, x)) % 360) + 360) % 360;
}

/**
 * Compute cumulative segment distances along a LineString.
 * Returns an array of cumulative distances (in meters), starting from 0.
 */
export function computeSegmentDistances(coordinates: number[][]): number[] {
  const distances = [0];
  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1];
    const [lon2, lat2] = coordinates[i];
    distances.push(
      distances[i - 1] + haversineDistance(lon1, lat1, lon2, lat2),
    );
  }
  return distances;
}

/**
 * Interpolate a point along a LineString at a given fraction (0 to 1) of total distance.
 */
function interpolateAtFraction(
  coordinates: number[][],
  cumulativeDistances: number[],
  fraction: number,
): { longitude: number; latitude: number } {
  const totalDistance = cumulativeDistances[cumulativeDistances.length - 1];
  const targetDistance = fraction * totalDistance;

  // Find the segment containing the target distance
  let segIdx = 0;
  for (let i = 1; i < cumulativeDistances.length; i++) {
    if (cumulativeDistances[i] >= targetDistance) {
      segIdx = i - 1;
      break;
    }
  }

  const segStart = cumulativeDistances[segIdx];
  const segEnd = cumulativeDistances[segIdx + 1];
  const segLength = segEnd - segStart;

  if (segLength === 0) {
    return {
      longitude: coordinates[segIdx][0],
      latitude: coordinates[segIdx][1],
    };
  }

  const t = (targetDistance - segStart) / segLength;
  const [lon1, lat1] = coordinates[segIdx];
  const [lon2, lat2] = coordinates[segIdx + 1];

  return {
    longitude: lon1 + t * (lon2 - lon1),
    latitude: lat1 + t * (lat2 - lat1),
  };
}

/**
 * Generate evenly distributed fractions for a given sampling config and total path distance/duration.
 */
function computeFractions(
  sampling: SamplingConfig,
  totalDistanceM: number,
  durationS: number,
): number[] {
  let count: number;

  switch (sampling.mode) {
    case 'count':
      count = Math.max(2, sampling.value);
      break;
    case 'time_interval':
      count = Math.max(2, Math.floor(durationS / sampling.value_s) + 1);
      break;
    case 'distance_interval':
      count = Math.max(2, Math.floor(totalDistanceM / sampling.value_m) + 1);
      break;
    case 'original':
      // Handled before this function is called; fall back to path vertex count
      count = 2;
      break;
    case 'ais_realistic':
      // Generate at 1-second resolution; the AIS sampler will thin it later
      count = Math.max(2, Math.floor(durationS) + 1);
      break;
  }

  const fractions: number[] = [];
  for (let i = 0; i < count; i++) {
    fractions.push(i / (count - 1));
  }
  return fractions;
}

/**
 * Interpolate points along a GeoJSON LineString based on the sampling configuration.
 *
 * @param coordinates - Array of [longitude, latitude] coordinate pairs
 * @param sampling - Sampling configuration (by count, time interval, or distance interval)
 * @param durationS - Total duration in seconds (needed for time_interval mode)
 * @returns Array of interpolated points with position, fraction, and bearing
 */
export function interpolatePath(
  coordinates: number[][],
  sampling: SamplingConfig,
  durationS: number,
): InterpolatedPoint[] {
  if (coordinates.length < 2) {
    throw new Error('Path must have at least 2 coordinates');
  }

  const cumulativeDistances = computeSegmentDistances(coordinates);
  const totalDistance = cumulativeDistances[cumulativeDistances.length - 1];

  if (totalDistance === 0) {
    throw new Error('Path has zero length');
  }

  const fractions = computeFractions(sampling, totalDistance, durationS);
  const points: InterpolatedPoint[] = [];

  for (let i = 0; i < fractions.length; i++) {
    const { longitude, latitude } = interpolateAtFraction(
      coordinates,
      cumulativeDistances,
      fractions[i],
    );
    points.push({
      longitude,
      latitude,
      fraction: fractions[i],
      bearing_deg: 0,
      sog_kn: 0,
      rot_deg_min: 0,
    });
  }

  // Calculate bearings
  for (let i = 0; i < points.length; i++) {
    if (i < points.length - 1) {
      points[i].bearing_deg = calculateBearing(
        points[i].longitude,
        points[i].latitude,
        points[i + 1].longitude,
        points[i + 1].latitude,
      );
    } else {
      // Last point uses bearing from previous point
      points[i].bearing_deg = points[i - 1].bearing_deg;
    }
  }

  return points;
}

/**
 * Get the total distance of a LineString in meters.
 */
export function getPathDistance(coordinates: number[][]): number {
  const distances = computeSegmentDistances(coordinates);
  return distances[distances.length - 1];
}

const KNOTS_TO_MPS = 0.514444;

/**
 * Compute total travel time in seconds implied by per-segment SOG values.
 * Useful for comparing against the scenario time range.
 */
export function computeTrackTravelTimeS(
  coordinates: number[][],
  waypoints: WaypointAttributes[],
): number {
  let total = 0;
  for (let i = 0; i < coordinates.length - 1; i++) {
    const dist = haversineDistance(
      coordinates[i][0],
      coordinates[i][1],
      coordinates[i + 1][0],
      coordinates[i + 1][1],
    );
    const speedMps = waypoints[i].sog_kn * KNOTS_TO_MPS;
    total += speedMps > 0 ? dist / speedMps : 0;
  }
  return total;
}

/**
 * Compute per-segment travel times from waypoint SOG values and segment distances,
 * or use explicit waypoint timestamps if available.
 * Returns an array of cumulative time fractions (0 to 1) per vertex.
 */
function computeTimeFractions(
  segDistances: number[],
  waypoints: WaypointAttributes[],
): number[] {
  // If waypoints have explicit timestamps, use them directly
  if (
    waypoints.length >= 2 &&
    waypoints[0].timestamp &&
    waypoints[waypoints.length - 1].timestamp
  ) {
    const startMs = new Date(waypoints[0].timestamp).getTime();
    const endMs = new Date(waypoints[waypoints.length - 1].timestamp).getTime();
    const range = endMs - startMs;
    if (range > 0) {
      return waypoints.map((wp) => {
        const ms = new Date(wp.timestamp).getTime();
        return (ms - startMs) / range;
      });
    }
  }

  // Fallback: derive from SOG + distances
  const segCount = segDistances.length - 1;
  const segTimes: number[] = [];
  let totalTime = 0;

  for (let i = 0; i < segCount; i++) {
    const dist = segDistances[i + 1] - segDistances[i];
    const speedMps = waypoints[i].sog_kn * KNOTS_TO_MPS;
    const time = speedMps > 0 ? dist / speedMps : 0;
    segTimes.push(time);
    totalTime += time;
  }

  // Convert to cumulative fractions
  const fractions = [0];
  let cumulative = 0;
  for (let i = 0; i < segCount; i++) {
    cumulative += segTimes[i];
    fractions.push(totalTime > 0 ? cumulative / totalTime : (i + 1) / segCount);
  }
  return fractions;
}

/**
 * Interpolate a point within the path at a given time fraction, using per-segment time allocation.
 * Returns position, the segment index, and fractional position within that segment.
 */
function interpolateAtTimeFraction(
  coordinates: number[][],
  timeFractions: number[],
  fraction: number,
): {
  longitude: number;
  latitude: number;
  segIndex: number;
  segFraction: number;
} {
  // Find which segment this time fraction falls in
  let segIdx = 0;
  for (let i = 1; i < timeFractions.length; i++) {
    if (timeFractions[i] >= fraction) {
      segIdx = i - 1;
      break;
    }
    segIdx = i - 1;
  }

  const segStart = timeFractions[segIdx];
  const segEnd = timeFractions[segIdx + 1];
  const segLen = segEnd - segStart;
  const t = segLen > 0 ? (fraction - segStart) / segLen : 0;

  const [lon1, lat1] = coordinates[segIdx];
  const [lon2, lat2] = coordinates[segIdx + 1];

  return {
    longitude: lon1 + t * (lon2 - lon1),
    latitude: lat1 + t * (lat2 - lat1),
    segIndex: segIdx,
    segFraction: t,
  };
}

/** Linearly interpolate between two numbers. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Linearly interpolate an angle in degrees, taking the shortest rotation path.
 * Handles the 0°/360° wraparound correctly.
 */
function lerpAngle(a: number, b: number, t: number): number {
  let diff = ((((b - a) % 360) + 540) % 360) - 180;
  return (((a + diff * t) % 360) + 360) % 360;
}

/**
 * Compute the number of sample points for a single segment given its sampling config.
 */
function computeSegmentSampleCount(
  sampling: SamplingConfig,
  segDistanceM: number,
  segDurationS: number,
): number {
  switch (sampling.mode) {
    case 'count':
      return Math.max(2, sampling.value);
    case 'time_interval':
      return Math.max(2, Math.floor(segDurationS / sampling.value_s) + 1);
    case 'distance_interval':
      return Math.max(2, Math.floor(segDistanceM / sampling.value_m) + 1);
    case 'original':
      return 2;
    case 'ais_realistic':
      // 1-second resolution base; sampler thins later
      return Math.max(2, Math.floor(segDurationS) + 1);
  }
}

/**
 * Build InterpolatedPoints directly from stored coordinates and fractions,
 * bypassing all interpolation. Used for imported tracks (mode: 'original').
 */
function buildOriginalPoints(
  coordinates: number[][],
  waypoints: WaypointAttributes[],
  fractions: number[],
): InterpolatedPoint[] {
  const points: InterpolatedPoint[] = [];

  for (let i = 0; i < coordinates.length; i++) {
    const [lon, lat] = coordinates[i];
    const wp = waypoints[i];

    points.push({
      longitude: lon,
      latitude: lat,
      fraction: fractions[i] ?? i / (coordinates.length - 1),
      bearing_deg: 0,
      sog_kn: wp.sog_kn,
      rot_deg_min: wp.rot_deg_min,
      name: wp.name ?? null,
      mmsi: wp.mmsi ?? null,
      imo: wp.imo ?? null,
      nav_status: wp.nav_status ?? null,
      heading_deg: wp.heading_deg ?? null,
      callSign: wp.callSign ?? null,
      flag: wp.flag ?? null,
      shipType: wp.shipType ?? null,
      aisClass: wp.aisClass ?? null,
      width_m: wp.width_m ?? null,
      length_m: wp.length_m ?? null,
      draught_m: wp.draught_m ?? null,
      destination: wp.destination ?? null,
      eta: wp.eta ?? null,
      waypointType: wp.type,
    });
  }

  // Calculate bearings (separate loop — all points exist now)
  for (let i = 0; i < points.length; i++) {
    if (i < points.length - 1) {
      points[i].bearing_deg = calculateBearing(
        points[i].longitude,
        points[i].latitude,
        points[i + 1].longitude,
        points[i + 1].latitude,
      );
    } else if (i > 0) {
      points[i].bearing_deg = points[i - 1].bearing_deg;
    }
  }

  return points;
}

/**
 * Interpolate points along a path using per-waypoint speed attributes.
 * Time is distributed proportionally to distance/sog per segment, so slow segments
 * get more positions and fast segments fewer.
 *
 * Numeric attributes (sog, heading, rot) are linearly interpolated between
 * consecutive waypoints for smooth transitions. Discrete attributes (nav_status,
 * name, mmsi, imo) snap at the segment boundary.
 *
 * Waypoints may carry an optional `sampling` override. When present, that segment
 * uses its own sampling density instead of the track-level default.
 */
export function interpolatePathWithWaypoints(
  coordinates: number[][],
  waypoints: WaypointAttributes[],
  sampling: SamplingConfig,
  durationS: number,
): InterpolatedPoint[] {
  if (coordinates.length < 2) {
    throw new Error('Path must have at least 2 coordinates');
  }

  // Original mode: return exact stored coordinates with pre-computed time fractions
  if (sampling.mode === 'original') {
    return buildOriginalPoints(coordinates, waypoints, sampling.fractions);
  }

  const segDistances = computeSegmentDistances(coordinates);
  const totalDistance = segDistances[segDistances.length - 1];
  if (totalDistance === 0) {
    throw new Error('Path has zero length');
  }

  const timeFractions = computeTimeFractions(segDistances, waypoints);
  const hasPerSegmentSampling = waypoints.some((wp) => wp.sampling != null);
  const points: InterpolatedPoint[] = [];

  if (hasPerSegmentSampling) {
    // Per-segment sampling: each segment computes its own sample points
    const segCount = coordinates.length - 1;
    for (let seg = 0; seg < segCount; seg++) {
      const segSampling = waypoints[seg].sampling ?? sampling;
      const segDistM = segDistances[seg + 1] - segDistances[seg];
      const segTimeFracStart = timeFractions[seg];
      const segTimeFracEnd = timeFractions[seg + 1];
      const segDurationS = (segTimeFracEnd - segTimeFracStart) * durationS;
      const count = computeSegmentSampleCount(
        segSampling,
        segDistM,
        segDurationS,
      );

      // Generate evenly spaced fractions within this segment's time range
      // Skip the last point of each segment (except the final one) to avoid duplicates
      const isLast = seg === segCount - 1;
      const steps = isLast ? count : count - 1;
      for (let i = 0; i < steps; i++) {
        const localT = i / (count - 1);
        const globalFrac =
          segTimeFracStart + localT * (segTimeFracEnd - segTimeFracStart);

        const { longitude, latitude } = interpolateAtTimeFraction(
          coordinates,
          timeFractions,
          globalFrac,
        );
        const wp = waypoints[seg];
        const nextWp = waypoints[Math.min(seg + 1, waypoints.length - 1)];

        const headingA = wp.heading_deg ?? null;
        const headingB = nextWp.heading_deg ?? null;

        points.push({
          longitude,
          latitude,
          fraction: globalFrac,
          bearing_deg: 0,
          sog_kn: lerp(wp.sog_kn, nextWp.sog_kn, localT),
          rot_deg_min: lerp(wp.rot_deg_min, nextWp.rot_deg_min, localT),
          name: wp.name ?? null,
          mmsi: wp.mmsi ?? null,
          imo: wp.imo ?? null,
          nav_status: wp.nav_status ?? null,
          heading_deg:
            headingA != null && headingB != null
              ? lerpAngle(headingA, headingB, localT)
              : headingA,
          callSign: wp.callSign ?? null,
          flag: wp.flag ?? null,
          shipType: wp.shipType ?? null,
          aisClass: wp.aisClass ?? null,
          width_m: wp.width_m ?? null,
          length_m: wp.length_m ?? null,
          draught_m: wp.draught_m ?? null,
          destination: wp.destination ?? null,
          eta: wp.eta ?? null,
          waypointType: wp.type,
        });
      }
    }
  } else {
    // Global sampling: uniform fractions across the entire path (original behavior)
    const sampleFractions = computeFractions(
      sampling,
      totalDistance,
      durationS,
    );

    for (const frac of sampleFractions) {
      const { longitude, latitude, segIndex, segFraction } =
        interpolateAtTimeFraction(coordinates, timeFractions, frac);

      const wp = waypoints[segIndex];
      const nextWp = waypoints[Math.min(segIndex + 1, waypoints.length - 1)];
      const t = segFraction;

      const headingA = wp.heading_deg ?? null;
      const headingB = nextWp.heading_deg ?? null;

      points.push({
        longitude,
        latitude,
        fraction: frac,
        bearing_deg: 0,
        sog_kn: lerp(wp.sog_kn, nextWp.sog_kn, t),
        rot_deg_min: lerp(wp.rot_deg_min, nextWp.rot_deg_min, t),
        name: wp.name ?? null,
        mmsi: wp.mmsi ?? null,
        imo: wp.imo ?? null,
        nav_status: wp.nav_status ?? null,
        heading_deg:
          headingA != null && headingB != null
            ? lerpAngle(headingA, headingB, t)
            : headingA,
        callSign: wp.callSign ?? null,
        flag: wp.flag ?? null,
        shipType: wp.shipType ?? null,
        aisClass: wp.aisClass ?? null,
        width_m: wp.width_m ?? null,
        length_m: wp.length_m ?? null,
        draught_m: wp.draught_m ?? null,
        destination: wp.destination ?? null,
        eta: wp.eta ?? null,
        waypointType: wp.type,
      });
    }
  }

  // Calculate bearings
  for (let i = 0; i < points.length; i++) {
    if (i < points.length - 1) {
      points[i].bearing_deg = calculateBearing(
        points[i].longitude,
        points[i].latitude,
        points[i + 1].longitude,
        points[i + 1].latitude,
      );
    } else {
      points[i].bearing_deg = points[i - 1].bearing_deg;
    }
  }

  return points;
}
